// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: proactive assistant suggestions for the chats the user is looking at
// (action chips, docs/konvo-assistant/action-chips.md).
//
// Received messages: the chat feeds its loaded messages in (consider for Konvo
// channels, considerWhatsApp for WhatsApp chats); when new messages have
// arrived since the last look, the recent window goes to the server for a
// one-shot assistant turn. Returned suggestions are kept per chat until acted
// on, dismissed or expired, and the newest one becomes a chip on its message
// (receivedChipFor).
//
// The server also looks as messages arrive (ready-list.md point 4) and keeps the result: a chat
// that opens first reads that (GET /konvo/claude/chat-suggestions) and a look that finishes while
// it is open comes as KONVO_CHAT_SUGGESTIONS. The app only looks itself at what the server has
// not analysed (an older server, a skipped look, the reader's Assistant switched off there).
//
// Drafts: a composer reports its text (noteDraft). After a pause a free local
// gate checks that a colleague is named together with an ask, then the server
// is asked for at most one suggestion about the draft; it becomes the chip in
// the composer strip. Once that chip is used or dismissed, its words are set
// aside and the rest of the draft may get a second chip. A chip still showing
// when the message is sent comes back once in the Sent notice, and what was
// created gathers in the "1 task" pill by Send.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {Channel} from '@app/features/channel/models/Channel';
import type {
	ActionChipController,
	ActionChipHooks,
	CreatedAction,
} from '@app/features/konvo_assistant/components/action_chip/state/ActionChipRun';
import {draftStillHolds, findHandover, withoutHandled} from '@app/features/konvo_assistant/utils/DraftHandover';
import KonvoConnections, {type KonvoProactiveOutcome} from '@app/features/konvo_connections/state/KonvoConnections';
import type {MailMeetingAction} from '@app/features/konvo_mail/state/MailMeeting';
import type {TaskSuggestion} from '@app/features/messaging/components/modals/KonvoCreateTaskModal';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {ChannelTypes} from '@konvo/constants/src/ChannelConstants';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

const ENABLED_KEY = 'konvo.assistant.chatSuggestions';
const DISMISSED_KEY = 'konvo.assistant.chatSuggestions.dismissed';
const SEEN_KEY = 'konvo.assistant.chatSuggestions.seen';
const RECEIVED_KEY = 'konvo.assistant.chatSuggestions.received';
const CONTEXT_WINDOW = 20;
const FIRST_LOOK_MAX_FRESH = 8;
/** A first look only offers chips for what came in the last two hours: older asks were seen elsewhere. */
const FIRST_LOOK_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const DEBOUNCE_MS = 3500;
/** While the server looks at arrivals, a message in the open chat waits this long for its push. */
const SERVER_GRACE_MS = 30_000;
/** A chat's stored record is read again at most this often. */
const SYNC_REUSE_MS = 5000;
/** A chat that was just opened already holds what arrived while it was closed: look almost at once. */
const OPEN_DELAY_MS = 600;
/** How long typing has to pause before the draft is looked at. */
export const DRAFT_PAUSE_MS = 2500;
const DRAFT_CONTEXT = 12;
const BUSY_RETRY_MS = 15_000;
const MAX_DISMISSED = 300;
const MAX_SEEN_CHANNELS = 200;
/** Received suggestions kept across reloads: the newest chats, for a few days. */
const MAX_STORED_CHATS = 40;
const STORED_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
const SYSTEM_USER_ID = '0';

export interface SuggestionPerson {
	name: string;
	address: string;
}

/** What a chip can do. `task_me` is a task for the reader; `none` is "Nothing to do here". */
export type ActionKind = 'task' | 'reminder' | 'event' | 'block_time' | 'meeting';
export type ChoiceKey = 'task' | 'task_me' | 'reminder' | 'event' | 'block_time' | 'meeting' | 'none';

/** A `meeting` chip ("when can we meet?"): the times from the calendars and the reply to send. */
export interface MeetingChipOption extends MailMeetingAction {
	/** The reply with [[time]] (maybe [[backup]]) where the chosen times go. */
	template: string;
	/** The reply with the best free time written in. */
	reply: string;
}

export interface ActionTaskOption {
	name: string;
	description: string;
	/** ISO instant, or null when no due date was stated. */
	due: string | null;
	priority: number | null;
	assignee: {name: string; email: string | null; me: boolean};
	/** The finished task form (list, ClickUp assignee id), when the server could pick a list. */
	draft?: TaskSuggestion;
}

/** Every action the chip menu can switch to, derived on the server from the one drafted request. */
export interface SuggestionOptions {
	task?: ActionTaskOption;
	task_me?: ActionTaskOption;
	reminder?: {remind_at: string; note: string};
	event?: {title: string; start: string; end: string; attendees: Array<SuggestionPerson>};
	block_time?: {title: string; start: string; end: string};
	meeting?: MeetingChipOption;
}

/** Present when the user's habits, not the model, picked the kind. The client words it. */
export interface SuggestionWhy {
	chosen: ChoiceKey;
	count: number;
	scope: 'sender' | 'chat' | 'domain';
	scope_name: string;
}

/** Action-chip extras (all optional, so older payloads still parse). */
export interface SuggestionExtras {
	/** Exact substring of the anchor message (or draft) the action came from. */
	quote?: string | null;
	/** Scopes the choice is learned under, most specific first ("sender:<user id>", "chat:<id or jid>"). */
	learn_scopes?: Array<string>;
	/** Human names for those scopes; echoed back with a choice so the server can word `learned`. */
	scope_names?: Record<string, string>;
	why?: SuggestionWhy | null;
	options?: SuggestionOptions;
}

/** `item_id` ties a suggestion to the proactive engine's record, so its outcome can be reported. */
export type AssistantSuggestion = {item_id?: string} & SuggestionExtras &
	(
		| {
				kind: 'event';
				id: string;
				message_id: string;
				title: string;
				start: string;
				end: string;
				attendees: Array<SuggestionPerson>;
				summary: string;
		  }
		| {kind: 'reminder'; id: string; message_id: string; remind_at: string; note: string; summary: string}
		| {
				kind: 'task';
				id: string;
				message_id: string;
				name: string;
				summary: string;
				description?: string;
				/** The finished form: "Create task" opens it without another assistant run. */
				draft?: TaskSuggestion;
		  }
		| {kind: 'block_time'; id: string; message_id: string; title: string; start: string; end: string; summary: string}
		| {kind: 'meeting'; id: string; message_id: string; topic: string; template: string; lang: string; summary: string}
	);

/** Where a draft is being typed. */
export type DraftSource = 'chat' | 'whatsapp' | 'mail';

/** A composer's request for a look at its draft. */
export interface DraftWatch {
	/** The Konvo channel id, or `whatsapp:<jid>`. */
	key: string;
	/** What the server gets as channel_id: the Konvo channel id, or the WhatsApp jid. */
	channelId: string;
	source: DraftSource;
	channelName: string | null;
	/** The draft as displayed (mentions as "@Name"). */
	text: string;
	/** Recent messages, for context only. Called once, when the request goes out. */
	context: () => Array<SuggestionContextEntry>;
	/** Names of the people the draft could hand work to (chat members, colleagues). */
	names: () => ReadonlyArray<string>;
	/** Builds the chip that acts on a returned suggestion (it knows where the draft is). */
	createChip: ChipFactory;
	/** Extra request fields, e.g. `mail: {subject, to, cc, from}` for the mail editor. */
	extra?: () => Record<string, unknown>;
}

export type ChipFactory = (
	suggestion: AssistantSuggestion,
	people: Array<SuggestionPerson>,
	hooks: ActionChipHooks,
) => ActionChipController;

/** The Sent notice (D1): a chip that was still offering its action when the message went out. */
export interface SentNotice {
	entry: DraftSuggestion;
	/** The chat's name, for "Sent to #dev"; null for a plain "Sent". */
	sentTo: string | null;
	/** Bumps each time, so the notice's timer restarts. */
	id: number;
}

/** A chip about a received message: the message it sits on and its controller. */
export interface ReceivedChip {
	key: string;
	suggestion: AssistantSuggestion;
	chip: ActionChipController;
	/** The user replied after it or scrolled past it: the chip shrinks to a mark. */
	readPast: boolean;
}

/** One WhatsApp message, as the chat hands it to considerWhatsApp. */
export interface WhatsAppContextMessage {
	id: string;
	text: string;
	sentAt: string;
	mine: boolean;
	author: string;
	authorId: string;
}

/** The one suggestion about a composer's draft, and the chip that acts on it. */
export interface DraftSuggestion {
	key: string;
	suggestion: AssistantSuggestion;
	/** The draft text the suggestion was made from. */
	text: string;
	people: Array<SuggestionPerson>;
	chip: ActionChipController;
}

export interface SuggestionContextEntry {
	id: string;
	author: string;
	author_id: string;
	content: string;
	timestamp: string;
	mine: boolean;
	fresh: boolean;
}

interface ChannelSuggestions {
	suggestions: Array<AssistantSuggestion>;
	/** Newest message id the suggestions were computed for. */
	messageId: string;
	/** Chat participants the server could resolve to addresses (for inviting). */
	people: Array<SuggestionPerson>;
	/** When the suggestions arrived (epoch ms), so stored ones age out. */
	at?: number;
}

/** A chat's received suggestions as kept in storage, with the send times of their messages. */
interface StoredChannelSuggestions extends ChannelSuggestions {
	times: Record<string, number>;
}

export type SuggestionIntent = 'event' | 'reminder' | 'task';

interface SuggestResponse {
	suggestions?: Array<AssistantSuggestion>;
	people?: Array<SuggestionPerson>;
}

/** What the server found for a chat as its messages arrived (GET, and the gateway push). */
export interface StoredChatSuggestions {
	chat_key: string;
	/** The newest message the server looked at; null when it has not. */
	analysed_id: string | null;
	suggestions?: Array<AssistantSuggestion>;
	people?: Array<SuggestionPerson>;
	/** GET only: whether the server looks at this reader's chats as messages arrive. */
	watching?: boolean;
	/**
	 * The server skipped looks (its daily cap, a failed turn) after `from` (null: from the start):
	 * analysed_id moved past messages no model saw, so the app looks at those itself.
	 */
	skipped?: {from: string | null};
}

/** Received suggestions the app knows how to show (the server sends `meeting` only to apps that do). */
const ACCEPT = ['meeting'];

/** The same chip, whoever made it (the server's look or the app's own): one per message and kind. */
const chipKey = (suggestion: Pick<AssistantSuggestion, 'message_id' | 'kind'>): string =>
	`m:${suggestion.message_id}:${suggestion.kind}`;

/**
 * Merges a look's suggestions into a chat's. A suggestion with the id of one already there replaces
 * it; one for a message and kind already offered under another id (the server and the app both
 * looked) is left out, so the same chip never shows twice. `added` are the ones to give chips.
 */
export function mergeSuggestions(
	previous: ReadonlyArray<AssistantSuggestion>,
	incoming: ReadonlyArray<AssistantSuggestion>,
): {merged: Array<AssistantSuggestion>; added: Array<AssistantSuggestion>} {
	const ids = new Set(incoming.map((suggestion) => suggestion.id));
	const kept = previous.filter((old) => !ids.has(old.id));
	const taken = new Set(kept.map(chipKey));
	const added: Array<AssistantSuggestion> = [];
	for (const next of incoming) {
		if (taken.has(chipKey(next))) continue;
		taken.add(chipKey(next));
		added.push(next);
	}
	return {merged: [...kept, ...added], added};
}

const compareIds = (a: string, b: string): number => (BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0);

function readJson<T>(key: string, fallback: T): T {
	try {
		const raw = AppStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : fallback;
	} catch {
		return fallback;
	}
}

/**
 * Builds the transcript window sent to the server. Pure so it can be tested:
 * the last CONTEXT_WINDOW messages with text, marking as fresh those newer than
 * `seenId`, or — when the channel has never been looked at — the handful of
 * recent messages from the last few hours.
 */
export function buildSuggestionContext(
	messages: ReadonlyArray<Message>,
	seenId: string | null,
	currentUserId: string | null,
	now: number = Date.now(),
): Array<SuggestionContextEntry> {
	const window = messages
		.filter(
			(message) => message.content.trim().length > 0 && message.isUserMessage() && message.author.id !== SYSTEM_USER_ID,
		)
		.slice(-CONTEXT_WINDOW);
	let freshBudget = seenId ? Number.POSITIVE_INFINITY : FIRST_LOOK_MAX_FRESH;
	const entries: Array<SuggestionContextEntry> = [];
	for (let index = window.length - 1; index >= 0; index -= 1) {
		const message = window[index];
		const isNewer = seenId
			? compareIds(message.id, seenId) > 0
			: now - message.timestamp.getTime() <= FIRST_LOOK_MAX_AGE_MS;
		const fresh = isNewer && freshBudget > 0;
		if (fresh) freshBudget -= 1;
		entries.unshift({
			id: message.id,
			author: message.author.globalName ?? message.author.username,
			author_id: message.author.id,
			content: message.content,
			timestamp: message.timestamp.toISOString(),
			mine: currentUserId != null && message.author.id === currentUserId,
			fresh,
		});
	}
	return entries;
}

/**
 * The WhatsApp window: like buildSuggestionContext, but WhatsApp ids carry no
 * order, so "newer than seen" means "after the seen message in the list".
 */
export function buildWhatsAppContext(
	messages: ReadonlyArray<WhatsAppContextMessage>,
	seenId: string | null,
	now: number = Date.now(),
): Array<SuggestionContextEntry> {
	const window = messages.filter((message) => message.text.trim().length > 0).slice(-CONTEXT_WINDOW);
	const seenAt = seenId ? window.findIndex((message) => message.id === seenId) : -1;
	let freshBudget = seenAt >= 0 ? Number.POSITIVE_INFINITY : FIRST_LOOK_MAX_FRESH;
	const entries: Array<SuggestionContextEntry> = [];
	for (let index = window.length - 1; index >= 0; index -= 1) {
		const message = window[index]!;
		const isNewer = seenAt >= 0 ? index > seenAt : now - Date.parse(message.sentAt) <= FIRST_LOOK_MAX_AGE_MS;
		const fresh = isNewer && freshBudget > 0;
		if (fresh) freshBudget -= 1;
		entries.unshift({
			id: message.id,
			author: message.author,
			author_id: message.authorId,
			content: message.text,
			timestamp: message.sentAt,
			mine: message.mine,
			fresh,
		});
	}
	return entries;
}

export class AssistantSuggestionsStore {
	enabled = AppStorage.getItem(ENABLED_KEY) !== 'false';
	/**
	 * Received suggestions per chat. Kept in storage like the seen watermark: the
	 * watermark stops a second look at the same messages, so a suggestion lost
	 * to a reload (an app the phone suspended, say) would never come back.
	 */
	byChannel = new Map<string, ChannelSuggestions>();
	pendingChannelId: string | null = null;
	/** Chips on received messages, by suggestion id. */
	receivedChips = new Map<string, ActionChipController>();
	/** Suggestion ids whose chip has shrunk to a mark (read past, or replied to). */
	readPast = new Set<string>();
	/** The suggestion about each composer's draft, by DraftWatch key. */
	drafts = new Map<string, DraftSuggestion>();
	/** The Sent notice per composer key. */
	sentNotices = new Map<string, SentNotice>();
	/** What chips created in each composer's current draft ("1 task" by Send). */
	draftCreated = new Map<string, Array<CreatedAction>>();
	private dismissed = new Set<string>(readJson<Array<string>>(DISMISSED_KEY, []));
	private seen = new Map<string, string>(Object.entries(readJson<Record<string, string>>(SEEN_KEY, {})));
	private timers = new Map<string, ReturnType<typeof setTimeout>>();
	private inFlight = new Set<string>();
	/** When each analysed message was sent, so the newest asking message gets the chip. */
	private messageTimes = new Map<string, number>();
	private draftTimers = new Map<string, ReturnType<typeof setTimeout>>();
	/** The latest text each composer reported. */
	private draftText = new Map<string, string>();
	/** The text last sent to the server, so a pause without edits does not ask twice. */
	private draftAsked = new Map<string, string>();
	/** Words of chips already used or dismissed in the current draft; the gate looks past them. */
	private draftHandled = new Map<string, Array<string>>();
	private draftInFlight = new Set<string>();
	private noticeCounter = 0;
	/** Each open chat's chip maker, so suggestions the server pushes get their chips. */
	private chipFactories = new Map<string, ChipFactory>();
	/** Whether the server looks at arrivals for this reader (null until it said). */
	private serverWatching: boolean | null = null;
	private syncs = new Map<string, {at: number; promise: Promise<void>}>();
	/** WhatsApp ids carry no order: each open chat's message ids, oldest first. */
	private whatsAppOrder = new Map<string, Array<string>>();
	/** This device's switch was checked against the server's arrival look this session. */
	private arrivalSynced = false;

	constructor() {
		this.restoreReceived();
		makeAutoObservable<
			this,
			| 'dismissed'
			| 'seen'
			| 'timers'
			| 'inFlight'
			| 'statusRequested'
			| 'messageTimes'
			| 'draftTimers'
			| 'draftText'
			| 'draftAsked'
			| 'draftHandled'
			| 'draftInFlight'
			| 'noticeCounter'
			| 'chipFactories'
			| 'serverWatching'
			| 'syncs'
			| 'whatsAppOrder'
			| 'arrivalSynced'
		>(
			this,
			{
				dismissed: false,
				seen: false,
				timers: false,
				inFlight: false,
				statusRequested: false,
				messageTimes: false,
				draftTimers: false,
				draftText: false,
				draftAsked: false,
				draftHandled: false,
				draftInFlight: false,
				noticeCounter: false,
				chipFactories: false,
				serverWatching: false,
				syncs: false,
				whatsAppOrder: false,
				arrivalSynced: false,
			},
			{autoBind: true},
		);
		// On start: once the Assistant's settings are in, the server's look follows this switch.
		reaction(
			() => KonvoConnections.proactiveSettings != null,
			(loaded) => {
				if (loaded) this.syncArrivalSwitch();
			},
			{fireImmediately: true},
		);
	}

	/**
	 * The server looks at chats as messages arrive only for people whose app has "Suggestions in
	 * chats" on (proactive.chat_arrival, off by default). Written once per session after the
	 * settings load, and only when it differs; every toggle writes it too (setEnabled).
	 */
	syncArrivalSwitch(): void {
		const settings = KonvoConnections.proactiveSettings;
		if (this.arrivalSynced || !settings) return;
		this.arrivalSynced = true;
		if ((settings.chat_arrival === true) === this.enabled) return;
		void KonvoConnections.updateProactiveSettings({chat_arrival: this.enabled}).catch(() => {
			this.arrivalSynced = false;
		});
	}

	/** One switch (Settings → Assistant → Suggestions in chats) covers draft and received suggestions. */
	setEnabled(value: boolean): void {
		this.enabled = value;
		AppStorage.setItem(ENABLED_KEY, String(value));
		// The server's look as messages arrive follows the same switch.
		this.arrivalSynced = true;
		void KonvoConnections.updateProactiveSettings({chat_arrival: value}).catch(() => undefined);
		if (!value) {
			for (const timer of this.timers.values()) clearTimeout(timer);
			this.timers.clear();
			this.byChannel.clear();
			this.receivedChips.clear();
			AppStorage.removeItem(RECEIVED_KEY);
			for (const timer of this.draftTimers.values()) clearTimeout(timer);
			this.draftTimers.clear();
			this.drafts.clear();
			this.sentNotices.clear();
		}
	}

	// ── Drafts (the composer strip) ─────────────────────────────────────────

	/** The suggestion about what is being typed in this composer, if any. */
	draftFor(key: string): DraftSuggestion | null {
		return this.enabled ? (this.drafts.get(key) ?? null) : null;
	}

	noticeFor(key: string): SentNotice | null {
		return this.sentNotices.get(key) ?? null;
	}

	clearNotice(key: string, id?: number): void {
		const notice = this.sentNotices.get(key);
		if (notice && (id == null || notice.id === id)) this.sentNotices.delete(key);
	}

	createdFor(key: string): ReadonlyArray<CreatedAction> {
		return this.draftCreated.get(key) ?? EMPTY_CREATED;
	}

	/**
	 * Called by a composer on every change of its text. Drops a suggestion whose
	 * words are gone, and DRAFT_PAUSE_MS after typing stops runs the local gate
	 * (past any words already handled) and, only if it passes, asks the server.
	 */
	noteDraft(watch: DraftWatch): void {
		const {key, text} = watch;
		this.draftText.set(key, text);
		const timer = this.draftTimers.get(key);
		if (timer) {
			clearTimeout(timer);
			this.draftTimers.delete(key);
		}
		const entry = this.drafts.get(key);
		if (!text.trim()) {
			this.resetDraft(key);
			if (entry && entry.chip.phase === 'idle') this.drafts.delete(key);
			return;
		}
		if (entry) {
			// One at a time: a chip that is showing (or just did its thing) stays until its words go.
			if (entry.chip.phase !== 'idle' || draftStillHolds(entry.suggestion.quote ?? null, entry.text, text)) return;
			this.drafts.delete(key);
		}
		if (!this.enabled) return;
		this.draftTimers.set(
			key,
			setTimeout(() => {
				this.draftTimers.delete(key);
				void this.lookAtDraft(watch);
			}, DRAFT_PAUSE_MS),
		);
	}

	/**
	 * The message was sent. Clears the draft's state; a chip that was still
	 * offering its action comes back once as the Sent notice. Returns it too.
	 */
	draftSent(key: string, sentTo: string | null = null, notice = true): DraftSuggestion | null {
		const timer = this.draftTimers.get(key);
		if (timer) clearTimeout(timer);
		this.draftTimers.delete(key);
		this.draftText.set(key, '');
		this.resetDraft(key);
		const entry = this.drafts.get(key) ?? null;
		if (!entry) return null;
		this.drafts.delete(key);
		if (entry.chip.phase !== 'idle') return null;
		// Mail shows its own notice (the editor closes on send).
		if (!notice) return entry;
		this.noticeCounter += 1;
		this.sentNotices.set(key, {entry, sentTo, id: this.noticeCounter});
		return entry;
	}

	/** × on the chip: gone for this draft, and the engine hears it was dismissed. */
	dismissDraft(key: string): void {
		const entry = this.drafts.get(key);
		if (!entry) return;
		if (entry.suggestion.item_id) KonvoConnections.reportProactiveOutcome(entry.suggestion.item_id, 'dismissed');
		this.drafts.delete(key);
		this.handle(key, entry);
	}

	/** Sent or emptied: the next draft starts clean (handled words, the created pill). */
	private resetDraft(key: string): void {
		this.draftAsked.delete(key);
		this.draftHandled.delete(key);
		this.draftCreated.delete(key);
	}

	/** The chip's words are dealt with; the gate looks past them from now on. */
	private handle(key: string, entry: DraftSuggestion): void {
		const handled = this.draftHandled.get(key) ?? [];
		handled.push(entry.suggestion.quote?.trim() || entry.text);
		this.draftHandled.set(key, handled);
	}

	private async lookAtDraft(watch: DraftWatch): Promise<void> {
		const {key} = watch;
		const text = this.draftText.get(key) ?? '';
		if (!this.enabled || text !== watch.text) return;
		if (this.draftInFlight.has(key) || this.drafts.has(key)) return;
		const remaining = withoutHandled(text, this.draftHandled.get(key) ?? []);
		if (this.draftAsked.get(key) === remaining) return;
		if (!findHandover(remaining, watch.names())) return;
		if (!KonvoConnections.claude && !KonvoConnections.codex) {
			await KonvoConnections.refreshAssistant().catch(() => undefined);
		}
		if (!this.available) return;
		this.draftAsked.set(key, remaining);
		this.draftInFlight.add(key);
		try {
			const context = watch
				.context()
				.slice(-DRAFT_CONTEXT)
				.map((entry) => ({...entry, fresh: false}));
			const response = await http.post<SuggestResponse>(Endpoints.KONVO_CLAUDE_SUGGEST_ACTIONS, {
				body: {
					context,
					channel_name: watch.channelName,
					channel_id: watch.channelId,
					source: watch.source,
					draft: {text: remaining},
					...(watch.extra?.() ?? {}),
				},
				timeoutMs: 60_000,
			});
			const suggestion = response.body?.suggestions?.[0];
			if (!suggestion || !this.enabled) return;
			// Typing went on while the model thought: keep it only if its words are still there.
			if (!draftStillHolds(suggestion.quote ?? null, remaining, this.draftText.get(key) ?? '')) return;
			if (this.drafts.has(key)) return;
			const people = response.body?.people ?? [];
			const entry: DraftSuggestion = {key, suggestion, text: remaining, people, chip: null as never};
			const chip = watch.createChip(suggestion, people, {
				onActed: () => this.handle(key, entry),
				onGone: (reason) =>
					runInAction(() => {
						if (this.drafts.get(key)?.chip !== chip) return;
						this.drafts.delete(key);
						if (reason === 'folded' && chip.created) {
							this.draftCreated.set(key, [...this.createdFor(key), chip.created]);
						} else if (reason === 'none') {
							this.handle(key, entry);
						}
					}),
			});
			entry.chip = chip;
			runInAction(() => {
				this.drafts.set(key, entry);
			});
		} catch {
			// Best effort: the strip just stays empty.
		} finally {
			this.draftInFlight.delete(key);
		}
	}

	// ── Received messages (chips on messages) ───────────────────────────────

	/** Whether the selected assistant can serve suggestions right now. */
	get available(): boolean {
		const status = KonvoConnections.assistantProvider === 'codex' ? KonvoConnections.codex : KonvoConnections.claude;
		return status?.connected === true && status.enabled === true;
	}

	peopleFor(channelId: string): Array<SuggestionPerson> {
		return this.byChannel.get(channelId)?.people ?? [];
	}

	/**
	 * Right-click flow: ask for one specific kind of suggestion from one message.
	 * Returns null when the assistant is unavailable or produced nothing usable.
	 */
	async requestFor(
		channel: Channel,
		messages: ReadonlyArray<Message>,
		message: Message,
		intent: SuggestionIntent,
		currentUserId: string | null,
	): Promise<{suggestion: AssistantSuggestion | null; people: Array<SuggestionPerson>}> {
		if (!KonvoConnections.claude && !KonvoConnections.codex) {
			await KonvoConnections.refreshAssistant().catch(() => undefined);
		}
		if (!this.available) return {suggestion: null, people: []};
		const index = messages.findIndex((entry) => entry.id === message.id);
		const window = index === -1 ? [message] : messages.slice(Math.max(0, index - 12), index + 4);
		const context = buildSuggestionContext(window, null, currentUserId).map((entry) => ({
			...entry,
			fresh: entry.id === message.id,
		}));
		try {
			const response = await http.post<SuggestResponse>(Endpoints.KONVO_CLAUDE_SUGGEST_ACTIONS, {
				body: {context, channel_name: channel.name || null, intent, target_id: message.id},
				timeoutMs: 90_000,
			});
			const people = response.body?.people ?? [];
			const suggestion = (response.body?.suggestions ?? []).find((entry) => entry.kind === intent) ?? null;
			if (people.length > 0) {
				runInAction(() => {
					const entry = this.byChannel.get(channel.id);
					if (entry) this.byChannel.set(channel.id, {...entry, people});
				});
			}
			return {suggestion, people};
		} catch {
			return {suggestion: null, people: []};
		}
	}

	/** Suggestions from received messages (kept for the chips on messages). */
	receivedSuggestionsFor(channelId: string): Array<AssistantSuggestion> {
		return this.suggestionsFor(channelId);
	}

	suggestionsFor(channelId: string): Array<AssistantSuggestion> {
		const now = Date.now();
		return (this.byChannel.get(channelId)?.suggestions ?? []).filter((suggestion) => !this.expired(suggestion, now));
	}

	/**
	 * The one chip a chat shows on its messages: the newest asking message's
	 * suggestion that still has a live chip. Null when suggestions are off.
	 */
	receivedChipFor(key: string): ReceivedChip | null {
		if (!this.enabled) return null;
		let best: {suggestion: AssistantSuggestion; chip: ActionChipController; at: number; order: number} | null = null;
		this.suggestionsFor(key).forEach((suggestion, order) => {
			const chip = this.receivedChips.get(suggestion.id);
			if (!chip || chip.phase === 'gone') return;
			const at = this.messageTimes.get(suggestion.message_id) ?? 0;
			if (!best || at > best.at || (at === best.at && order > best.order)) best = {suggestion, chip, at, order};
		});
		const found = best as {suggestion: AssistantSuggestion; chip: ActionChipController} | null;
		if (!found) return null;
		return {key, suggestion: found.suggestion, chip: found.chip, readPast: this.readPast.has(found.suggestion.id)};
	}

	/** The reader replied after it or scrolled past it: shrink the chip to a mark. */
	markReadPast(suggestionId: string): void {
		if (!this.readPast.has(suggestionId)) this.readPast.add(suggestionId);
	}

	/**
	 * Called by the Konvo chat whenever its messages change. Debounced so a burst
	 * of messages produces one analysis of the whole burst.
	 */
	consider(
		channel: Channel,
		messages: ReadonlyArray<Message>,
		currentUserId: string | null,
		createChip?: ChipFactory,
		opening = false,
	): void {
		this.ensureStatus();
		if (!this.enabled || messages.length === 0) return;
		if (channel.type === ChannelTypes.DM && channel.recipientIds.includes(SYSTEM_USER_ID)) return;
		if (createChip) {
			this.chipFactories.set(channel.id, createChip);
			this.restoreChips(channel.id, createChip);
		}
		for (const message of messages.slice(-CONTEXT_WINDOW))
			this.messageTimes.set(message.id, message.timestamp.getTime());
		// What the server found while the chat was closed shows at once, whatever the look decides.
		if (opening) void this.syncStored(channel.id, true);
		const newest = messages[messages.length - 1];
		const seenId = this.seen.get(channel.id) ?? null;
		if (seenId && compareIds(newest.id, seenId) <= 0) return;
		// Do not react to the reader's own message: nothing new to suggest from it,
		// and it would re-run the model after every reply they send.
		if (currentUserId && newest.author.id === currentUserId && seenId) {
			this.markSeen(channel.id, newest.id);
			return;
		}
		this.schedule(
			channel.id,
			() => {
				const context = buildSuggestionContext(messages, this.seen.get(channel.id) ?? null, currentUserId);
				return {
					newestId: newest.id,
					context,
					body: {context, channel_name: channel.name || null, channel_id: channel.id, source: 'chat', accept: ACCEPT},
					createChip,
				};
			},
			opening ? OPEN_DELAY_MS : this.arrivalDelay(),
		);
	}

	/** The same for a WhatsApp chat: `key` is "whatsapp:<jid>", channel_id the jid. */
	considerWhatsApp(params: {
		key: string;
		jid: string;
		name: string | null;
		messages: ReadonlyArray<WhatsAppContextMessage>;
		createChip?: ChipFactory;
		/** The chat was just opened (see consider). */
		opening?: boolean;
	}): void {
		this.ensureStatus();
		const {key, jid, name, messages, createChip, opening = false} = params;
		if (!this.enabled || messages.length === 0) return;
		if (createChip) {
			this.chipFactories.set(key, createChip);
			this.restoreChips(key, createChip);
		}
		for (const message of messages.slice(-CONTEXT_WINDOW))
			this.messageTimes.set(message.id, Date.parse(message.sentAt));
		this.whatsAppOrder.set(
			key,
			messages.slice(-CONTEXT_WINDOW * 3).map((message) => message.id),
		);
		if (opening) void this.syncStored(key, true);
		const newest = messages[messages.length - 1]!;
		const seenId = this.seen.get(key) ?? null;
		if (seenId === newest.id) return;
		if (newest.mine && seenId) {
			this.markSeen(key, newest.id);
			return;
		}
		this.schedule(
			key,
			() => {
				const context = buildWhatsAppContext(messages, this.seen.get(key) ?? null);
				return {
					newestId: newest.id,
					context,
					body: {context, channel_name: name, channel_id: jid, source: 'whatsapp', accept: ACCEPT},
					createChip,
				};
			},
			opening ? OPEN_DELAY_MS : this.arrivalDelay(),
		);
	}

	dismiss(channelId: string, suggestionId: string): void {
		this.reportOutcome(channelId, suggestionId, 'dismissed');
		this.retire(channelId, suggestionId);
	}

	/** Gone for good (dismissed, used, nothing to do): never offered again for this message. */
	retire(channelId: string, suggestionId: string): void {
		this.dismissed.add(suggestionId);
		// Also its message and kind: another look's copy under a new id is not offered again.
		const gone = this.byChannel.get(channelId)?.suggestions.find((suggestion) => suggestion.id === suggestionId);
		if (gone) this.dismissed.add(chipKey(gone));
		while (this.dismissed.size > MAX_DISMISSED) {
			const oldest = this.dismissed.values().next().value;
			if (!oldest) break;
			this.dismissed.delete(oldest);
		}
		AppStorage.setItem(DISMISSED_KEY, JSON.stringify([...this.dismissed]));
		this.remove(channelId, suggestionId);
	}

	/** The prefilled form was saved: tell the engine it helped, then drop the suggestion. */
	markUsed(channelId: string, suggestionId: string): void {
		this.reportOutcome(channelId, suggestionId, 'used');
		this.remove(channelId, suggestionId);
	}

	/** Drops a suggestion after the user acted on it (or dismissed it). */
	remove(channelId: string, suggestionId: string): void {
		this.receivedChips.delete(suggestionId);
		const entry = this.byChannel.get(channelId);
		if (!entry) return;
		this.byChannel.set(channelId, {
			...entry,
			suggestions: entry.suggestions.filter((suggestion) => suggestion.id !== suggestionId),
		});
		this.persistReceived();
	}

	private statusRequested = false;

	/** The menu entries and the chips depend on the connection status; fetch it once per session. */
	ensureStatus(): void {
		if (this.statusRequested || KonvoConnections.claude || KonvoConnections.codex) return;
		this.statusRequested = true;
		void KonvoConnections.refreshAssistant().catch(() => {
			this.statusRequested = false;
		});
	}

	private reportOutcome(channelId: string, suggestionId: string, outcome: KonvoProactiveOutcome): void {
		const itemId = this.byChannel.get(channelId)?.suggestions.find((entry) => entry.id === suggestionId)?.item_id;
		if (itemId) KonvoConnections.reportProactiveOutcome(itemId, outcome);
	}

	private expired(suggestion: AssistantSuggestion, now: number): boolean {
		if (suggestion.kind === 'event' || suggestion.kind === 'block_time') return Date.parse(suggestion.start) < now;
		if (suggestion.kind === 'reminder') return Date.parse(suggestion.remind_at) < now;
		if (suggestion.kind === 'meeting') {
			const times = Object.values(suggestion.options?.meeting?.options ?? {}).flat();
			return times.length > 0 && times.every((option) => Date.parse(option.start) < now);
		}
		return false;
	}

	// ── The server's looks ──────────────────────────────────────────────────

	/** A message in an open chat waits for the server's push when the server is looking. */
	private arrivalDelay(): number {
		return this.serverWatching ? SERVER_GRACE_MS : DEBOUNCE_MS;
	}

	/** Gateway KONVO_CHAT_SUGGESTIONS: a look the server finished for one of the reader's chats. */
	applyPush(event: StoredChatSuggestions): void {
		this.serverWatching = true;
		this.applyStored(event.chat_key, event);
	}

	/**
	 * Reads what the server keeps for a chat and merges it. Reused for a few seconds; skipped
	 * when the server said it does not look (unless `force`, when a chat opens).
	 */
	syncStored(key: string, force = false): Promise<void> {
		const last = this.syncs.get(key);
		if (last && Date.now() - last.at < SYNC_REUSE_MS) return last.promise;
		if (!force && this.serverWatching === false) return Promise.resolve();
		const promise = http
			.get<StoredChatSuggestions>(Endpoints.KONVO_CLAUDE_CHAT_SUGGESTIONS, {query: {chat: key}, timeoutMs: 15_000})
			.then((response) => {
				const body = response.body;
				if (!body || typeof body !== 'object') return;
				this.serverWatching = body.watching === true;
				this.applyStored(key, body);
			})
			.catch(() => {
				// An older server: the app keeps looking itself.
				if (this.serverWatching == null) this.serverWatching = false;
			});
		this.syncs.set(key, {at: Date.now(), promise});
		if (this.syncs.size > MAX_SEEN_CHANNELS) this.syncs.delete(this.syncs.keys().next().value!);
		return promise;
	}

	/** Merges a server look like one of the app's own: the chips, the people, the seen mark. */
	applyStored(key: string, record: StoredChatSuggestions): void {
		if (!this.enabled || typeof record.analysed_id !== 'string' || !record.analysed_id) return;
		const analysedId = record.analysed_id;
		const now = Date.now();
		// The server keeps the newest first; the chip goes on the last one, so oldest first here.
		const incoming = (Array.isArray(record.suggestions) ? record.suggestions : [])
			.filter((suggestion) => suggestion && typeof suggestion.id === 'string' && !this.isDismissed(suggestion))
			.filter((suggestion) => !this.expired(suggestion, now))
			.reverse();
		// Looked at up to analysed_id, or only up to where the server started skipping (the app's own
		// look then covers the rest).
		const lookedTo = record.skipped ? record.skipped.from : analysedId;
		if (lookedTo && this.isAfterSeen(key, lookedTo)) this.markSeen(key, lookedTo);
		runInAction(() => {
			const current = this.byChannel.get(key);
			if (!current && incoming.length === 0) return;
			const people = Array.isArray(record.people) && record.people.length ? record.people : (current?.people ?? []);
			const {merged, added} = mergeSuggestions(current?.suggestions ?? [], incoming);
			this.byChannel.set(key, {
				suggestions: merged.filter((suggestion) => !this.expired(suggestion, now)),
				messageId: analysedId,
				people,
				at: now,
			});
			const factory = this.chipFactories.get(key);
			if (factory) for (const suggestion of added) this.attachChip(key, suggestion, people, factory);
			this.persistReceived();
		});
	}

	/** Whether a message id is newer than the chat's seen mark (WhatsApp: later in the open chat). */
	private isAfterSeen(key: string, id: string): boolean {
		const seen = this.seen.get(key);
		if (!seen) return true;
		if (/^\d+$/.test(id) && /^\d+$/.test(seen)) return compareIds(id, seen) > 0;
		const order = this.whatsAppOrder.get(key);
		if (!order) return false;
		const at = order.indexOf(id);
		return at >= 0 && at > order.indexOf(seen);
	}

	/** The newest message has been looked at, by the app or the server. */
	private covered(key: string, newestId: string): boolean {
		const seen = this.seen.get(key);
		if (!seen) return false;
		return /^\d+$/.test(newestId) && /^\d+$/.test(seen) ? compareIds(newestId, seen) <= 0 : seen === newestId;
	}

	/** Dismissed, used or "nothing to do": by id, or by its message and kind. */
	private isDismissed(suggestion: AssistantSuggestion): boolean {
		return this.dismissed.has(suggestion.id) || this.dismissed.has(chipKey(suggestion));
	}

	private markSeen(channelId: string, messageId: string): void {
		this.seen.set(channelId, messageId);
		while (this.seen.size > MAX_SEEN_CHANNELS) {
			const oldest = this.seen.keys().next().value;
			if (!oldest) break;
			this.seen.delete(oldest);
		}
		AppStorage.setItem(SEEN_KEY, JSON.stringify(Object.fromEntries(this.seen)));
	}

	/** Gives a received suggestion its chip, unless it has one. */
	private attachChip(
		key: string,
		suggestion: AssistantSuggestion,
		people: Array<SuggestionPerson>,
		createChip: ChipFactory,
	): void {
		if (this.receivedChips.has(suggestion.id)) return;
		this.receivedChips.set(
			suggestion.id,
			createChip(suggestion, people, {
				// Used, dismissed or "nothing to do": this message is done with.
				onGone: (reason) => {
					if (reason === 'dismissed') this.dismiss(key, suggestion.id);
					else this.retire(key, suggestion.id);
				},
			}),
		);
	}

	/** Suggestions restored from storage get their chips once their chat is open again. */
	private restoreChips(key: string, createChip: ChipFactory): void {
		const entry = this.byChannel.get(key);
		if (!entry) return;
		for (const suggestion of this.suggestionsFor(key)) {
			if (!this.isDismissed(suggestion)) this.attachChip(key, suggestion, entry.people, createChip);
		}
	}

	private restoreReceived(): void {
		const stored = readJson<Record<string, StoredChannelSuggestions>>(RECEIVED_KEY, {});
		const now = Date.now();
		for (const [key, entry] of Object.entries(stored)) {
			if (!entry || !Array.isArray(entry.suggestions) || now - (entry.at ?? 0) > STORED_MAX_AGE_MS) continue;
			const suggestions = entry.suggestions.filter(
				(suggestion) => !this.isDismissed(suggestion) && !this.expired(suggestion, now),
			);
			if (suggestions.length === 0) continue;
			this.byChannel.set(key, {
				suggestions,
				messageId: entry.messageId,
				people: entry.people ?? [],
				at: entry.at,
			});
			for (const [messageId, at] of Object.entries(entry.times ?? {})) this.messageTimes.set(messageId, at);
		}
	}

	private persistReceived(): void {
		const kept = [...this.byChannel.entries()]
			.filter(([, entry]) => entry.suggestions.length > 0)
			.sort(([, a], [, b]) => (b.at ?? 0) - (a.at ?? 0))
			.slice(0, MAX_STORED_CHATS);
		if (kept.length === 0) {
			AppStorage.removeItem(RECEIVED_KEY);
			return;
		}
		const out: Record<string, StoredChannelSuggestions> = {};
		for (const [key, entry] of kept) {
			const times: Record<string, number> = {};
			for (const suggestion of entry.suggestions) {
				const at = this.messageTimes.get(suggestion.message_id);
				if (at != null) times[suggestion.message_id] = at;
			}
			out[key] = {...entry, at: entry.at ?? Date.now(), times};
		}
		AppStorage.setItem(RECEIVED_KEY, JSON.stringify(out));
	}

	private schedule(key: string, prepare: () => PreparedLook, delay = DEBOUNCE_MS): void {
		const existing = this.timers.get(key);
		if (existing) clearTimeout(existing);
		this.timers.set(
			key,
			setTimeout(() => {
				this.timers.delete(key);
				void this.analyze(key, prepare);
			}, delay),
		);
	}

	private async analyze(key: string, prepare: () => PreparedLook): Promise<void> {
		if (!this.enabled) return;
		if (this.inFlight.has(key)) {
			// Messages that arrived during a look get their own look once it is done.
			this.schedule(key, prepare);
			return;
		}
		if (!KonvoConnections.claude && !KonvoConnections.codex) {
			await KonvoConnections.refreshAssistant().catch(() => undefined);
		}
		if (!this.available) return;
		// The server may have looked already (it looks as messages arrive): read that first.
		await this.syncStored(key);
		const look = prepare();
		if (this.covered(key, look.newestId)) return;
		if (!look.context.some((entry) => entry.fresh)) {
			this.markSeen(key, look.newestId);
			return;
		}
		for (const entry of look.context) this.messageTimes.set(entry.id, Date.parse(entry.timestamp));
		this.inFlight.add(key);
		runInAction(() => {
			this.pendingChannelId = key;
		});
		try {
			const response = await http.post<SuggestResponse>(Endpoints.KONVO_CLAUDE_SUGGEST_ACTIONS, {
				body: look.body,
				timeoutMs: 90_000,
			});
			const people = response.body?.people ?? [];
			const suggestions = (response.body?.suggestions ?? []).filter((suggestion) => !this.isDismissed(suggestion));
			this.markSeen(key, look.newestId);
			runInAction(() => {
				const {merged, added} = mergeSuggestions(this.byChannel.get(key)?.suggestions ?? [], suggestions);
				this.byChannel.set(key, {
					suggestions: merged.filter((suggestion) => !this.expired(suggestion, Date.now())),
					messageId: look.newestId,
					people,
					at: Date.now(),
				});
				if (look.createChip) {
					for (const suggestion of added) this.attachChip(key, suggestion, people, look.createChip);
				}
				this.persistReceived();
			});
		} catch (error) {
			const status = (error as {status?: number} | null)?.status;
			if (status === 429) {
				// Another tab or chat is being analysed for this user; look again shortly.
				this.schedule(key, prepare, BUSY_RETRY_MS);
			} else {
				// Best effort: never loop on a failing turn.
				this.markSeen(key, look.newestId);
			}
		} finally {
			this.inFlight.delete(key);
			runInAction(() => {
				if (this.pendingChannelId === key) this.pendingChannelId = null;
			});
		}
	}
}

interface PreparedLook {
	newestId: string;
	context: Array<SuggestionContextEntry>;
	body: Record<string, unknown>;
	createChip?: ChipFactory;
}

const EMPTY_CREATED: ReadonlyArray<CreatedAction> = [];

export default new AssistantSuggestionsStore();
